
import React from "react";
import { Button } from "@/components/ui/button";
import { Check, ExternalLink } from "lucide-react";
import { Logo } from "@/components/ui/logo";

interface Lesson {
  id: number;
  title: string;
  duration: string;
  completed: boolean;
  videoUrl: string;
  thumbnail: string;
}

interface Course {
  id: number;
  title: string;
  progress: number;
  image: string;
  instructor: string;
  instructorImage: string;
  description: string;
  lessons: Lesson[];
}

interface TutorialCourseProps {
  course: Course;
  viewMode: "grid" | "list";
  onSelect: () => void;
}

export const TutorialCourse: React.FC<TutorialCourseProps> = ({
  course,
  viewMode,
  onSelect
}) => {
  const createPlaceholderImage = (title: string) => {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-black/50 p-4 text-center">
        <div className="mb-4">
          <Logo variant="icon-only" size="lg" />
        </div>
        <h3 className="text-white font-semibold text-lg mb-2">{title}</h3>
        <p className="text-gray-300 text-sm">Hoss Immo Consulting Tutorial</p>
      </div>
    );
  };

  if (viewMode === "grid") {
    return (
      <div 
        className="glass-morphism rounded-xl overflow-hidden hover:border-[#41c7f0]/30 transition-all duration-300 cursor-pointer"
        onClick={onSelect}
      >
        <div className="relative aspect-[16/9] overflow-hidden">
          <div className="absolute inset-0 bg-black/30">
            {createPlaceholderImage(course.title)}
          </div>
        </div>
        
        <div className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-1.5 bg-gray-700 rounded-full flex-1">
              <div 
                className="h-full bg-[#41c7f0] rounded-full" 
                style={{ width: `${course.progress}%` }}
              />
            </div>
            <span className="text-sm font-medium text-white">{course.progress}% abgeschlossen</span>
          </div>
          
          <Button variant="default" className="w-full" onClick={onSelect}>
            {course.progress === 100 ? (
              <>
                <Check size={16} className="mr-2" /> Abgeschlossen
              </>
            ) : (
              <>
                <ExternalLink size={16} className="mr-2" /> Weitermachen
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }
  
  // List view
  return (
    <div 
      className="glass-morphism rounded-xl p-4 flex gap-4 hover:border-[#41c7f0]/30 transition-all duration-300 cursor-pointer"
      onClick={onSelect}
    >
      <div className="w-40 h-24 overflow-hidden rounded-lg flex-shrink-0 relative">
        <div className="absolute inset-0 bg-black/30">
          {createPlaceholderImage(course.title)}
        </div>
      </div>
      
      <div className="flex-1 flex flex-col">
        <h3 className="font-bold text-lg mb-2">{course.title}</h3>
        
        <div className="flex items-center gap-2 mb-auto">
          <div className="h-1.5 bg-gray-700 rounded-full flex-1 max-w-xs">
            <div 
              className="h-full bg-[#41c7f0] rounded-full" 
              style={{ width: `${course.progress}%` }}
            />
          </div>
          <span className="text-sm font-medium text-white">{course.progress}% abgeschlossen</span>
        </div>
        
        <Button variant="default" className="w-fit mt-2" onClick={onSelect}>
          {course.progress === 100 ? (
            <>
              <Check size={16} className="mr-2" /> Abgeschlossen
            </>
          ) : (
            <>
              <ExternalLink size={16} className="mr-2" /> Weitermachen
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
